import React, { useState, useEffect, useContext } from 'react';
import { intlFormat } from 'date-fns';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { useDataHelper } from 'utils/dataHelper';
import { useHistory } from 'react-router-dom';
import { useEmailHelper } from 'services/emailSender';

import { breakoutRoomInviteModel } from 'entities/breakoutRoomInviteModel';
import { breakoutRoomModel } from 'entities/breakoutRoomModel';
import { LoaderContext } from 'context/LoaderContextProvider';

import { SdmSpacer } from 'components';

import BrCardsContainer from '../components/BrCardsContainer';
import AcceptInvitationDialog from './AcceptInvitationDialog';
import DeclineInvitationDialog from './DeclineInvitationDialog';
import Grid from '@material-ui/core/Grid';

const UserArea = ({ user, detectedEmail = false, locale }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.personalArea(intl).userPage;
  const { sendEmail, emailEnums } = useEmailHelper();
  const { isLoaderActive, setLoaderActive } = useContext(LoaderContext);
  const dataHelper = useDataHelper();
  const history = useHistory();
  // const navigation = useNavigation();
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  // States
  const [fetchedBr, setFetchedBr] = useState();
  const [acceptInviteDialog, setAcceptInviteDialog] = useState({
    isOpen: false,
    text: '',
  });
  const [declineInviteDialog, setDeclineInviteDialog] = useState({
    isOpen: false,
  });

  // Effects

  // useEffect(() => {
  //   navigation.clearForward();
  // }, []);

  useEffect(() => {
    if (user) {
      showLoader();
      fetchBr();
    }
  }, [user]);

  useEffect(() => {
    if (fetchedBr) {
      if (detectedEmail && detectedEmail !== user.email)
        setAcceptInviteDialog({ isOpen: true, text: i18n.emailDialog });
      hideLoader();
    }
  }, [fetchedBr]);

  // Functions
  const fetchBr = async () => {
    const parsedBrByGroups = [];
    const userInvitations =
      await breakoutRoomInviteModel.gql.listUserInvitations(user.id);
    //Only Pending Invitations
    const onlyPendingInvitation =
      userInvitations?.myBreakoutRoomInvitations?.items?.filter(
        (i) => i?.status === breakoutRoomInviteModel.enums.status.PENDING
      );
    //Only Accepted Invitations
    const onlyAcceptedInvitation =
      userInvitations?.myBreakoutRoomInvitations?.items?.filter(
        (i) => i?.status === breakoutRoomInviteModel.enums.status.ACCEPTED
      );
    //Try to find if there any doubles
    const foundDoubled = onlyPendingInvitation.filter((i) =>
      onlyAcceptedInvitation.find(
        (inv) => inv?.breakoutRoom?.id === i?.breakoutRoom?.id
      )
    );
    //Create a 'blacklist' array of doubles id (from the Pending Array)
    const flatIds = foundDoubled?.map((i) => i.id);
    //Remove this invitation from the total
    const filteredInvitations =
      userInvitations?.myBreakoutRoomInvitations?.items?.filter(
        (i) => !flatIds.includes(i.id)
      );

    dataHelper
      .orderBreakoutRooms(
        filteredInvitations
          ?.filter(
            (invite) =>
              invite.status !== breakoutRoomInviteModel.enums.status.CANCELED
          )
          .map((invite) => {
            return { ...invite, start: invite.breakoutRoom.start };
          })
      )
      .map((invite) => {
        const br = invite.breakoutRoom;
        const parsedBr = {
          ...br,
          id: invite.id,
          status: invite.status,
          breakoutRoomId: br.id,
        };
        const date = intlFormat(
          new Date(br.date),
          {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          },
          {
            locale: dataHelper.getLangCode(intl.locale),
          }
        );
        if (!Object.keys(parsedBrByGroups).includes(date)) {
          parsedBrByGroups[date] = [parsedBr];
        } else {
          parsedBrByGroups[date].push(parsedBr);
        }
      });

    setFetchedBr(parsedBrByGroups);
  };

  const isChatType = (br) => {
    return br.type === breakoutRoomModel.enums.types.CHAT;
  };

  const updateInvitation = (data) => {
    const date = intlFormat(
      new Date(data.date),
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      {
        locale: dataHelper.getLangCode(intl.locale),
      }
    );
    const targetBr = fetchedBr[date].find((br) => br.id === data.id);
    const idx = fetchedBr[date].indexOf(targetBr);
    const _fetchedBr = { ...fetchedBr };
    _fetchedBr[date][idx] = data;
    setFetchedBr(_fetchedBr);
  };

  const onAccept = async (data) => {
    showLoader();
    setAcceptInviteDialog({ isOpen: true, text: i18n.acceptDialog });
    const input = {
      id: data.id,
      status: breakoutRoomInviteModel.enums.status.ACCEPTED,
    };
    await breakoutRoomInviteModel.gql.update(input);
    await sendEmail(emailEnums.ACCEPT_NOTIIFCATION, {
      breakoutRoom: data,
      targets: [user.email],
      langcode: locale,
      is_live_chat: isChatType(data),
    });
    updateInvitation({ ...data, ...input });
    hideLoader();
  };

  const onDecline = (data) => {
    setDeclineInviteDialog({ isOpen: true, data: data });
  };

  const onEnter = (brWithInvitation) => {
    if (brWithInvitation.type !== breakoutRoomModel.enums.types.CHAT)
      dataHelper.joinBr(brWithInvitation.breakoutRoomId, user?.email);
    else dataHelper.joinChat(brWithInvitation.breakoutRoomId);
  };

  const handleDecline = async (data) => {
    showLoader();
    const input = {
      id: data.id,
      note: data.note,
      status: breakoutRoomInviteModel.enums.status.REJECTED,
    };
    await breakoutRoomInviteModel.gql.update(input);
    updateInvitation({ ...data, ...input });
    hideLoader();
  };

  const onDetails = (invitation) => {
    history.push('/details', invitation);
  };

  const renderAcceptInvitationDialog = () => {
    if (!acceptInviteDialog.isOpen) return null;
    return (
      <AcceptInvitationDialog
        onClose={() => {
          setAcceptInviteDialog({ isOpen: false, text: '' });
        }}
        i18n={acceptInviteDialog.text}
      />
    );
  };

  const renderDeleteInviteDialog = () => {
    if (!declineInviteDialog.isOpen) return null;
    return (
      <DeclineInvitationDialog
        onAgree={handleDecline}
        onClose={() => {
          setDeclineInviteDialog({ isOpen: false });
        }}
        i18n={i18n.declineDialog}
        data={declineInviteDialog.data}
      />
    );
  };

  return (
    <Grid container>
      <div style={{ width: '100%' }}>
        {renderAcceptInvitationDialog()}
        {renderDeleteInviteDialog()}
        {/* <PageHeader i18n={i18n} isBrandPage={false} /> */}
        <div style={{ backgroundColor: fetchedBr ? '#f3f3f3' : 'white' }}>
          <div style={{ width: '80%', margin: '0 auto', maxWidth: 1250 }}>
            <SdmSpacer height={30} />
            {!isLoaderActive && fetchedBr ? (
              Object.keys(fetchedBr).length > 0 ? (
                Object.keys(fetchedBr).map((key, idx) => {
                  const orderedBr = dataHelper.orderBreakoutRooms(
                    fetchedBr[key]
                  );
                  return (
                    <BrCardsContainer
                      key={idx}
                      date={key}
                      functions={{
                        onAccept,
                        onDecline,
                        onDetails,
                        onEnter,
                      }}
                      items={orderedBr}
                    />
                  );
                })
              ) : (
                <div>{i18n.noBrAvailable}</div>
              )
            ) : null}
            <SdmSpacer height={30} />
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default UserArea;
